import { ReactNode, createContext, useCallback, useContext, useId, useState } from 'react';
import * as Radix from '@radix-ui/react-toast';
import { CheckCircle2, AlertCircle, Info, AlertTriangle, X } from 'lucide-react';
import { cn } from '../lib/cn';

export type ToastTone = 'success' | 'error' | 'info' | 'warning' | 'neutral';

interface ToastInstance {
  id: string;
  tone: ToastTone;
  title: string;
  message?: ReactNode;
  action?: { label: string; onClick: () => void };
  durationMs?: number;
}

interface ToastContextValue {
  push: (t: Omit<ToastInstance, 'id'>) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TONE_ICON: Record<ToastTone, ReactNode> = {
  success: <CheckCircle2 className="h-5 w-5 text-success" aria-hidden />,
  error: <AlertCircle className="h-5 w-5 text-error" aria-hidden />,
  warning: <AlertTriangle className="h-5 w-5 text-warning" aria-hidden />,
  info: <Info className="h-5 w-5 text-info" aria-hidden />,
  neutral: <Info className="h-5 w-5 text-ink-muted" aria-hidden />,
};

const TONE_BORDER: Record<ToastTone, string> = {
  success: 'border-l-success',
  error: 'border-l-error',
  warning: 'border-l-warning',
  info: 'border-l-info',
  neutral: 'border-l-border-strong',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastInstance[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback<ToastContextValue['push']>((t) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setToasts((prev) => [...prev.slice(-2), { ...t, id }]);
  }, []);

  return (
    <ToastContext.Provider value={{ push, dismiss }}>
      <Radix.Provider swipeDirection="right" duration={5000}>
        {children}
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
        <Radix.Viewport className="fixed bottom-0 right-0 z-[100] m-4 flex w-[380px] max-w-[calc(100vw-2rem)] flex-col gap-2 outline-none" />
      </Radix.Provider>
    </ToastContext.Provider>
  );
}

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: ToastInstance;
  onDismiss: () => void;
}) {
  const titleId = useId();
  return (
    <Radix.Root
      duration={toast.durationMs ?? (toast.tone === 'error' ? 10000 : 5000)}
      onOpenChange={(open) => {
        if (!open) onDismiss();
      }}
      className={cn(
        'flex items-start gap-3 rounded-md border border-border-soft border-l-4 bg-surface px-4 py-3 shadow-md',
        'data-[state=open]:animate-slide-up data-[state=closed]:animate-fade-in data-[state=closed]:opacity-0',
        TONE_BORDER[toast.tone],
      )}
    >
      <span className="mt-0.5 shrink-0">{TONE_ICON[toast.tone]}</span>
      <div className="min-w-0 flex-1">
        <Radix.Title id={titleId} className="text-base font-medium text-ink leading-ui">
          {toast.title}
        </Radix.Title>
        {toast.message ? (
          <Radix.Description className="mt-0.5 text-sm text-ink-secondary">
            {toast.message}
          </Radix.Description>
        ) : null}
      </div>
      {toast.action ? (
        <Radix.Action altText={toast.action.label} asChild>
          <button
            type="button"
            onClick={toast.action.onClick}
            className="shrink-0 text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-soft rounded-sm px-1"
          >
            {toast.action.label}
          </button>
        </Radix.Action>
      ) : null}
      <Radix.Close
        className="-mr-1 -mt-1 shrink-0 rounded-sm p-1 text-ink-muted transition-colors duration-fast hover:bg-surface-sunken hover:text-ink focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-soft"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" aria-hidden />
      </Radix.Close>
    </Radix.Root>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return ctx;
}
